/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.37.0
 * @since 1.37.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectBreadCrumbsData } from '@/main/modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@/main/modules/breadCrumbs/utils/breadCrumbsGoBack';
import { ModalMultipleOrders } from '@/main/modules/modal/components/ModalMultipleOrders';
import { HttpStatus } from '@modules/app/appConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardChangeRole,
} from '@modules/internationalization/intSelectors';
import { IWizardChangeRoleTranslation } from '@modules/internationalization/types/wizards/changeRole';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import {
    wizardReportStorageRestoreSaga,
    wizardReportValidateSaga,
} from '@modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import {
    wizardNextAction,
    wizardStorageSaveStepAction,
    wizardStorageUpdateStepAction,
} from '@modules/wizard/wizardActions';
import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@modules/wizard/wizardConstants';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@modules/wizard/wizardTypes';
import {
    IWizardChangeRoleOrderResult, IWizardChangeRoleReducer,
    IWizardChangeRoleSubsAccessLevel,
} from '@pages/WizardChangeRole/wizardChangeRoleReducer';

import { ICommonTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { makeSelectAuthoritiesIsServiceUser } from '../PageAuth/selectors/authSelectors';
import { wizardChangeRoleSetParamsAction, wizardChangeRoleSetStepValidAction } from './wizardChangeRoleActions';
import {
    EWizardChangeRoleSteps,
    WIZARD_CHANGE_ROLE_HANDLE_NEXT,
    WIZARD_CHANGE_ROLE_SET_PARAMS,
    WIZARD_CHANGE_ROLE_START_VALIDATE,
} from './wizardChangeRoleConstant';
import {
    makeSelectWizardChangeRoleOrderResult,
    makeSelectWizardChangeRoleRemovedSubscribers, makeSelectWizardChangeRoleSelectedAccessLevel,
    makeSelectWizardChangeRoleSubsAccessLevels,
    makeSelectWizardChangeRoleSubscriberIds,
} from './wizardChangeRoleSelectors';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* wizardChangeRoleMetaSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const localStorageParams = yield call(wizardChangeRoleLoadDataFromStorageSaga);

    if (!!localStorageParams.selectedAccessLevel) {
        yield put(
            wizardChangeRoleSetParamsAction({
                selectedAccessLevel: localStorageParams.selectedAccessLevel,
            }),
        );
    }

    switch (stepName) {
        case EWizardChangeRoleSteps.StepSelectParams:
            const subsAccessLevels: IWizardChangeRoleReducer['subsAccessLevels'] = action.payload.step.subsAccessLevels;
            yield put(
                wizardChangeRoleSetParamsAction({
                    subsAccessLevels: subsAccessLevels || [],
                }),
            );
            break;
        case EWizardChangeRoleSteps.StepConfirmation:
            yield call(wizardReportStorageRestoreSaga, action);
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

export function* wizardChangeRoleHandleNextSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    const subscribersIds: number[] = yield select(makeSelectWizardChangeRoleSubscriberIds());
    const selectedAccessLevel: IWizardChangeRoleSubsAccessLevel =
        yield select(makeSelectWizardChangeRoleSelectedAccessLevel());

    const params: { [key: string]: string | number | boolean | number[] } = {
        subsIds: subscribersIds,
        accessLevel: selectedAccessLevel.id,
    };

    switch (stepName) {
        case EWizardChangeRoleSteps.StepSelectParams:
            yield put(
                wizardNextAction({
                    data: params,
                }),
            );
            break;
        case EWizardChangeRoleSteps.StepConfirmation:
            const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

            yield put(
                wizardNextAction({
                    data: params,
                    ...reportParams,
                }),
            );
            break;
    }

    yield call(wizardChangeRoleUpdateStorageSaga);
}

export function* wizardChangeRoleHandleNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());
    switch (stepName) {
        case EWizardChangeRoleSteps.StepSelectParams:
            const orderResult: IWizardChangeRoleOrderResult = action.payload.currentStep?.step.orderResult;
            yield put(
                wizardChangeRoleSetParamsAction({
                    orderResult: orderResult,
                }),
            );
            break;
        case EWizardChangeRoleSteps.StepConfirmation:
            const orderId = action.payload.currentStep?.step?.order?.orderId ?? '';

            const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

            const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
            const goBack = () => breadCrumbGoBack(breadCrumbsData);

            yield put(
                modalOpen({
                    render: () =>
                        createElement(ModalMultipleOrders, {
                            isOpen: true,
                            orderOrOrders: orderId,
                            onClose: goBack,
                            onSuccessButtonClick: breadCrumbGoBack,
                            orderTypeTranlation: orderCreationModal.success.orderType.CHANGE_ROLE,
                        }),
                }),
            );

            break;
    }

    yield call(wizardChangeRoleLoadDataFromStorageSaga);
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardChangeRoleUpdateStorageSaga(): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardChangeRoleSteps.StepSelectParams:
            const subsAccessLevels = yield select(makeSelectWizardChangeRoleSubsAccessLevels());
            const selectedAccessLevel = yield select(makeSelectWizardChangeRoleSelectedAccessLevel());
            yield put(
                wizardStorageUpdateStepAction({
                    subsAccessLevels,
                    selectedAccessLevel,
                }),
            );
            break;
        case EWizardChangeRoleSteps.StepConfirmation:
            const orderResult = yield select(makeSelectWizardChangeRoleOrderResult());
            const removedSubscribers = yield select(makeSelectWizardChangeRoleRemovedSubscribers());
            yield put(
                wizardStorageUpdateStepAction({
                    orderResult,
                    removedSubscribers,
                }),
            );
            break;
    }
}

export function* wizardChangeRoleLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    /**
     * Если обновили страницу обновим всю информацию, которая у нас есть
     */
    if (localStorageParams) {
        const orderResult = yield select(makeSelectWizardChangeRoleOrderResult());
        const subsAccessLevels = yield select(makeSelectWizardChangeRoleSubsAccessLevels());
        const removedSubscribers = yield select(makeSelectWizardChangeRoleRemovedSubscribers());
        const selectedAccessLevel = yield select(makeSelectWizardChangeRoleSelectedAccessLevel());

        yield put(
            wizardChangeRoleSetParamsAction({
                orderResult: orderResult?.orderSubs.length ? orderResult : localStorageParams.orderResult,
                subsAccessLevels: subsAccessLevels?.length ? subsAccessLevels : localStorageParams.subsAccessLevels,
                removedSubscribers: removedSubscribers?.length
                    ? removedSubscribers
                    : localStorageParams.removedSubscribers || [],
                selectedAccessLevel: selectedAccessLevel ? selectedAccessLevel : localStorageParams.selectedAccessLevel,
            }),
        );
    }

    yield call(wizardChangeRoleUpdateStorageSaga);

    return localStorageParams;
}

export function* wizardChangeRoleStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

export function* wizardChangeRoleValidateSaga(): SagaIterator {
    let isStepValid = true;
    let validateError = '';
    const stepName = yield select(makeSelectWizardStepName());
    const translation: IWizardChangeRoleTranslation = yield select(makeSelectTranslationWizardChangeRole());
    const subscribersIds: number[] = yield select(makeSelectWizardChangeRoleSubscriberIds());
    const selectedAccessLevel: IWizardChangeRoleSubsAccessLevel =
        yield select(makeSelectWizardChangeRoleSelectedAccessLevel());

    switch (stepName) {
        case EWizardChangeRoleSteps.StepSelectParams:
            if (selectedAccessLevel?.id === 0) {
                isStepValid = false;
                validateError = translation.validateErrors.selectAccessLevel;
            }
            break;
        case EWizardChangeRoleSteps.StepConfirmation:
            const isServiceUser: boolean = yield select(makeSelectAuthoritiesIsServiceUser());
            if (isServiceUser) {
                const commonTranslation: ICommonTranslation = yield select(makeSelectTranslationCommon());
                validateError = commonTranslation.serviceUser.tooltip;
                isStepValid = false;
                break;
            }

            const isReportValid = yield call(wizardReportValidateSaga);
            isStepValid = subscribersIds.length > 0 && isReportValid;
            if (subscribersIds.length < 1) {
                validateError = translation.validateErrors.selectOneSubscribers;
                break;
            }
            validateError = isStepValid ? '' : translation.validateErrors.fillNotify;
            break;
    }
    yield put(
        wizardChangeRoleSetStepValidAction({
            isStepValid,
            validateError,
        }),
    );
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardChangeRoleFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        const translationExpensesDetails = yield select(makeSelectTranslationWizardChangeRole());
        title = translationExpensesDetails.error.codes[errorCode] || translationCommon.error.codes[errorCode];
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */

export function* wizardChangeRoleSagas(): SagaIterator {
    yield all([
        // initialization
        takeEvery(WIZARD_START_SUCCESS, wizardChangeRoleStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardChangeRoleMetaSaga),
        // next
        takeEvery(WIZARD_CHANGE_ROLE_HANDLE_NEXT, wizardChangeRoleHandleNextSaga),
        takeEvery(WIZARD_NEXT_SUCCESS, wizardChangeRoleHandleNextSuccessSaga),
        takeEvery(WIZARD_BACK_SUCCESS, wizardChangeRoleLoadDataFromStorageSaga),
        // validate
        takeEvery(
            [WIZARD_CHANGE_ROLE_START_VALIDATE, WIZARD_CHANGE_ROLE_SET_PARAMS],
            wizardChangeRoleValidateSaga,
        ),
        takeEvery(WIZARD_CHANGE_ROLE_SET_PARAMS, wizardChangeRoleUpdateStorageSaga),
        // failed
        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardChangeRoleFailedSaga,
        ),
    ]);
}
